import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Answer } from "types/answer";
import { GET_SURVEY_QUESTIONS_BY_SURVEY_ID } from "graphql/surveyQuestion/survey_question.query";
import { GET_ANSWERS_BY_QUESTION_ID } from "graphql/answer/answer.query";
import { GET_SURVEY_BY_ID } from "graphql/survey/survey.query";

/**
 * hook to share Survey Answers functionalities
 */
export const useSurveyAnswers = () => {
  const { surveyId, organizationId, submissionId } = useParams();
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [surveyName, setSurveyName] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  const [getSurveyQuestions] = useLazyQuery(GET_SURVEY_QUESTIONS_BY_SURVEY_ID);
  const [getAnswersByQuestionId] = useLazyQuery(GET_ANSWERS_BY_QUESTION_ID);
  const [getSurveyById] = useLazyQuery(GET_SURVEY_BY_ID);

  const fetchAnswers = useCallback(async () => {
    if (!surveyId) return;

    setLoading(true);
    setError(null);

    try {
      // First, get survey details to get the name
      const { data: surveyData } = await getSurveyById({
        variables: { surveyId },
      });
      
      if (surveyData?.survey_by_pk?.name) {
        setSurveyName(surveyData.survey_by_pk.name);
      }

      // Then, get all questions for this survey
      const { data: surveyQuestionsData } = await getSurveyQuestions({
        variables: { surveyId },
      });

      if (
        !surveyQuestionsData?.survey_question ||
        surveyQuestionsData.survey_question.length === 0
      ) {
        setAnswers([]);
        setLoading(false);
        return;
      }

      // Get all question IDs
      const questionIds = surveyQuestionsData.survey_question.map(
        (sq: any) => sq.question_id,
      );

      // Fetch answers for all questions
      const allAnswers: Answer[] = [];
      for (const questionId of questionIds) {
        const { data: answersData } = await getAnswersByQuestionId({
          variables: { questionId },
        });

        if (answersData?.answer) {
          const questionData = surveyQuestionsData.survey_question.find(
            (sq: any) => sq.question_id === questionId,
          );

          const answersForQuestion: Answer[] = (
            answersData.answer as any[]
          ).map((ans: any) => ({
            answer_id: ans.answer_id,
            answer: ans.answer,
            option_id: ans.option_id || undefined,
            question_id: ans.question_id,
            user_id: ans.user_id || undefined,
            created_at: ans.created_at || undefined,
            option: ans.option
              ? {
                  option_id: ans.option.option_id,
                  order: ans.option.order,
                  question_id: ans.option.question_id,
                  text: ans.option.text,
                }
              : undefined,
            question: questionData?.question
              ? {
                  question_id: questionData.question.question_id,
                  title: questionData.question.title,
                  type: questionData.question.type,
                  body: questionData.question.body,
                  order: questionData.question.order,
                  options: questionData.question.options?.map((opt: any) => ({
                    option_id: opt.option_id,
                    text: opt.text || "",
                    order: opt.order || 0,
                    question_id: questionData.question.question_id || "",
                  })) || [],
                }
              : undefined,
            user: ans.user
              ? {
                  user_id: ans.user.user_id,
                  name: ans.user.name || "",
                  email: ans.user.email || "",
                  picture_url: ans.user.picture_url || undefined,
                }
              : undefined,
          }));

          allAnswers.push(...answersForQuestion);
        }
      }

      // Filter answers for the specific submission if submissionId is provided
      const filteredAnswers = submissionId
        ? allAnswers.filter((answer) => {
            const timestamp = answer.created_at 
              ? new Date(answer.created_at).getTime().toString()
              : "0";
            const answerSubmissionKey = `${answer.user_id || "anonymous"}_${timestamp}`;
            return answerSubmissionKey === decodeURIComponent(submissionId);
          })
        : allAnswers;

      setAnswers(filteredAnswers);
    } catch (err: any) {
      setError(err);
      console.error("Error fetching answers:", err);
    } finally {
      setLoading(false);
    }
  }, [surveyId, getSurveyQuestions, getAnswersByQuestionId, getSurveyById]);

  useEffect(() => {
    fetchAnswers();
  }, [fetchAnswers]);

  return {
    answers,
    surveyName,
    loading,
    error,
    organizationId,
    surveyId,
    refetch: fetchAnswers,
  };
};
